
import { useState, useCallback } from 'react';
import { Funnel } from '../../../types';
import { ErrorHandler } from '../../../core/errors/ErrorHandler';
import {
    getFunnelsUseCase,
    createFunnelUseCase,
    updateFunnelUseCase,
    deleteFunnelUseCase
} from '../../../infrastructure/di/crmContainer';

const SELECTED_FUNNEL_KEY = 'adlitics_selected_funnel_id';

export const useFunnelManagement = (organizationId: string | undefined) => {
    const [funnels, setFunnels] = useState<Funnel[]>([]);

    // Inicializa com o funil salvo no localStorage
    const [selectedFunnelId, setSelectedFunnelIdState] = useState<string | null>(() => {
        try {
            return localStorage.getItem(SELECTED_FUNNEL_KEY);
        } catch {
            return null;
        }
    });

    // Wrapper para salvar no localStorage ao mudar o funil
    const setSelectedFunnelId = useCallback((id: string | null) => {
        setSelectedFunnelIdState(id);
        try {
            if (id) {
                localStorage.setItem(SELECTED_FUNNEL_KEY, id);
            } else {
                localStorage.removeItem(SELECTED_FUNNEL_KEY);
            }
        } catch (error) {
            console.error('Error saving funnel to localStorage:', error);
        }
    }, []);

    const refreshFunnels = useCallback(async () => {
        try {
            const data = await getFunnelsUseCase.execute(organizationId);
            setFunnels(data);

            setSelectedFunnelIdState(prev => {
                if (!prev && data.length > 0) {
                    try {
                        localStorage.setItem(SELECTED_FUNNEL_KEY, data[0].id);
                    } catch (e) {}
                    return data[0].id;
                }
                // Se já tem selecionado, mas não existe mais nessa org, pega o 1º tb
                if (prev && !data.some(f => f.id === prev) && data.length > 0) {
                     try { localStorage.setItem(SELECTED_FUNNEL_KEY, data[0].id); } catch(e) {}
                     return data[0].id;
                }
                return prev;
            });

        } catch (error) {
            ErrorHandler.handle(error as Error, 'RefreshFunnels');
        }
    }, [organizationId]);

    const createFunnel = useCallback(async (name: string) => {
        try {
            const newFunnel = await createFunnelUseCase.execute(name, organizationId);
            await refreshFunnels();
            if (newFunnel) {
                setSelectedFunnelId(newFunnel.id);
            }
            return newFunnel;
        } catch (error) {
            ErrorHandler.handle(error as Error, 'CreateFunnel');
            return null;
        }
    }, [refreshFunnels, organizationId, setSelectedFunnelId]);

    const updateFunnel = useCallback(async (id: string, name: string) => {
        try {
            await updateFunnelUseCase.execute(id, name);
            await refreshFunnels();
        } catch (error) {
            ErrorHandler.handle(error as Error, 'UpdateFunnel');
        }
    }, [refreshFunnels]);

    const deleteFunnel = useCallback(async (id: string) => {
        try {
            await deleteFunnelUseCase.execute(id);
            await refreshFunnels();
            if (selectedFunnelId === id) {
                setSelectedFunnelId(null);
            }
        } catch (error) {
            ErrorHandler.handle(error as Error, 'DeleteFunnel');
        }
    }, [refreshFunnels, selectedFunnelId, setSelectedFunnelId]);

    return {
        funnels,
        setFunnels,
        selectedFunnelId,
        setSelectedFunnelId,
        refreshFunnels,
        createFunnel,
        updateFunnel,
        deleteFunnel
    };
};
